import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import { ClientAddressService } from "../../services/ClientAddressService";
import { ClientAddressCreateDTO } from "../../models/ClientAddress/ClientAddressCreateDTO";
import Toast from "../../helpers/Toast";
import ButtonComponent from "../ButtonComponent";
import { ClientDTO } from "../../models/Client/ClientDTO";
import { CountryService } from "../../services/CountryService";
import { ICountryDTO } from "../../models/Country/ICountryDTO";
import SearchSelectComponent, { SelectItemsProps } from "../SearchSelectComponent";

type Props = {
  visible: boolean;
  onClose: () => void;
  onCreated: () => void;
  client: ClientDTO | null;
};

export default function CreateAddressPopup({
  visible,
  onClose,
  onCreated,
  client,
}: Props) {
  const intl = useIntl();
  const service = new ClientAddressService();
  const countryService = new CountryService();

  const [street1, setStreet1] = useState("");
  const [street2, setStreet2] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [countryId, setCountryId] = useState<number | "">("");

  const [countries, setCountries] = useState<ICountryDTO[]>([]);
  const [countrySelect, setCountrySelect] = useState<SelectItemsProps | null>(null);

  const searchCountry = async (nameCountry: string): Promise<SelectItemsProps[]> => {
    const response = await countryService.SearchCountryByName(nameCountry);
    if (!response.success || !response.obj) {
      Toast.Show("error", response.message);
      return [];
    }

    return response.obj.map((country) => ({
      value: country.id.toString(),
      label: country.name
    }));
  };



  const resetFields = () => {
    setStreet1("");
    setStreet2("");
    setPostalCode("");
    setCity("");
    setCountryId("");
  };

  useEffect(() => {
    const fetchCountries = async () => {
      const result = await countryService.GetAll([], [], 1, 1000);
      if (result && result.success && result.items) {
        setCountries(result.items);
      }
    };

    if (visible) {
      fetchCountries();
    }
  }, [visible]);

  const handleSave = async () => {
    if (!street1.trim()) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.Validation.AddressRequired" }));
      return;
    }

    if (!client) {
      Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.ClientRequired" }));
      return;
    }

    if (!countrySelect || !countrySelect.value) {
  Toast.Show("error", intl.formatMessage({ id: "Screens.ClientAddresses.Validation.CountryRequired" }));
  return;
}


    const newAddress: ClientAddressCreateDTO = {
      clientId: client.id,   
      street1,
      street2,
      postalCode,
      city,
      countryId: Number(countrySelect.value),  
    };

    const result = await service.create(newAddress);

    if (result && result.success) {
      Toast.Show("success", intl.formatMessage({ id: "Screens.ClientAddresses.CreatedSuccess" }));
      onCreated();
      resetFields();
      onClose();
    } else {
      Toast.Show("error", result?.message ?? intl.formatMessage({ id: "Screens.ClientAddresses.CreateError" }));
    }
  };


  return (
    <Modal isOpen={visible} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>
        {intl.formatMessage({ id: "Screens.ClientAddresses.CreateNewAddress" })}
      </ModalHeader>
      <ModalBody>
        <Form>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label for="street1">
                  {intl.formatMessage({ id: "Screens.ClientAddresses.Table.Address" })}
                </Label>
                <Input
                  id="street1"
                  value={street1}
                  onChange={(e) => setStreet1(e.target.value)}
                  placeholder={intl.formatMessage({ id: "Screens.ClientAddresses.Table.Address" })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="street2">
                  {intl.formatMessage({ id: "Screens.ClientAddresses.Table.AddressOptional" })}
                </Label>
                <Input
                  id="street2"
                  value={street2}
                  onChange={(e) => setStreet2(e.target.value)}
                  placeholder={intl.formatMessage({ id: "Screens.ClientAddresses.Table.AddressOptional" })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="postalCode">
                  {intl.formatMessage({ id: "Screens.ClientAddresses.Table.PostalCode" })}
                </Label>
                <Input
                  id="postalCode"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder={intl.formatMessage({ id: "Screens.ClientAddresses.Table.PostalCode" })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="city">
                  {intl.formatMessage({ id: "Screens.ClientAddresses.Table.City" })}
                </Label>
                <Input
                  id="city"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder={intl.formatMessage({ id: "Screens.ClientAddresses.Table.City" })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                
                <SearchSelectComponent
                  xl={12}
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  label={intl.formatMessage({ id: "Screens.ClientAddresses.SelectCountry" })}
                  loadOptions={searchCountry}
                  onChange={(e: SelectItemsProps) => setCountrySelect(e)}
                  value={countrySelect}
                  required
                  style={{ width: "100%" }}
                />

              </FormGroup>
            </Col>
          </Row>
        </Form>
      </ModalBody>
      <ModalFooter>
        <ButtonComponent
          onClick={onClose}
          style={{ minWidth: "100px" }}
          text={intl.formatMessage({ id: "Screens.ClientAddresses.Cancel" })}
        />
        <ButtonComponent
          onClick={handleSave}
          style={{ minWidth: "100px" }}
          text={intl.formatMessage({ id: "Screens.ClientAddresses.Save" })}
        />
      </ModalFooter>
    </Modal>
  );
}
